import React, { useEffect, useState } from 'react';
import layoutEngine from '../libraries/layoutEngine';
import ProjectManager from '../canvas/ProjectManager';
import {isLocked, toggleLock, toggleVisibility,isVisible} from '../canvas/VisibilityManager';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Tooltip } from '@mui/material';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';


/** Count how many times each child cell is referenced */
const getInstanceCounts = () => {
  const created = layoutEngine.getCells();
  const uploaded = layoutEngine.getUploadedCells();
  const cells = [...created, ...uploaded];

  const counts = {};
  for (const cell of cells) {
    for (const instance of cell.instances || []) {
      const key = `${cell.name}->${instance.ref}`;
      counts[key] = (counts[key] || 0) + 1;
    }
  }
  return counts;
};

/** Build hierarchy tree */
const buildHierarchyTree = () => {
  // const created = layoutEngine.getCells();
  // const uploaded = layoutEngine.getUploadedCells();
  // const cells = [...created, ...uploaded];
   const created = layoutEngine.getCells();
  const uploaded = layoutEngine.getUploadedCells();

  const createdNames = new Set(created.map(c => c.name));

  const cells = [
    ...created,
    ...uploaded.filter(c => !createdNames.has(c.name))
  ];

  const cellMap = new Map();
  cells.forEach(cell => {
    cellMap.set(cell.name, { name: cell.name, children: [] });
  });

  const counts = getInstanceCounts();

  // const buildChildrenRecursive = (parentNode, visited = new Set()) => {
  //   const cell = cells.find(c => c.name === parentNode.name);
  //   if (!cell || visited.has(parentNode.name)) return;

  //   visited.add(parentNode.name);
  //   const addedRefs = new Set();

  //   for (const instance of cell.instances || []) {
  //     const refName = instance.ref;
  //     if (addedRefs.has(refName)) continue;
  //     const childNode = cellMap.get(refName);
  //     if (childNode) {
  //       parentNode.children.push(childNode);
  //       addedRefs.add(refName);
  //       buildChildrenRecursive(childNode, visited);
  //     }
  //   }
  // };
  const buildChildrenRecursive = (parentNode, path = new Set()) => {
  const cell =
    layoutEngine.getCellByName(parentNode.name) ||
    layoutEngine.getUploadedCell(parentNode.name);

  if (!cell) return;

  if (path.has(parentNode.name)) return; // cycle protection

  const nextPath = new Set(path);
  nextPath.add(parentNode.name);

  const addedRefs = new Set();

  for (const instance of cell.instances || []) {
    const refName = instance.ref;
    if (addedRefs.has(refName)) continue;

    // ✅ IMPORTANT: create NEW node object
    const childNode = {
      name: refName,
      children: []
    };

    parentNode.children.push(childNode);
    addedRefs.add(refName);

    buildChildrenRecursive(childNode, nextPath);
  }
};

  const roots = [];
  for (const cell of cells) {
    if (!cells.some(c => (c.instances || []).some(i => i.ref === cell.name))) {
      const rootNode = cellMap.get(cell.name);
      buildChildrenRecursive(rootNode);
      roots.push(rootNode);
    }
  }

  return { roots, counts };
};

/** Get maximum hierarchy depth (like KLayout: topcell = 1, child = 2, etc.) */
const getMaxLevel = (cell, visited = new Set()) => {
  if (!cell || visited.has(cell.name)) return 0;
  visited.add(cell.name);

  let childMax = 0;
  for (const inst of cell.instances || []) {
    const instCell =
      layoutEngine.getCellByName(inst.ref) ||
      layoutEngine.getUploadedCell(inst.ref);
    if (instCell) {
      const depth = getMaxLevel(instCell, new Set(visited));
      childMax = Math.max(childMax, depth);
    }
  }
  return 1 + childMax;
};

/** Tree node component */
const TreeNode = ({ node, activeCell, onSelect, counts, parentName, level = 0, onUpdate ,searchTerm}) => {
  const [expanded, setExpanded] = useState(true);
  const [editing, setEditing] = useState(false);
  const [renameValue, setRenameValue] = useState(node.name);
  useEffect(() => {
  if (searchTerm && node.children && node.children.length > 0) {
    setExpanded(true);
  }
}, [searchTerm, node.children]);

  const saveRename = () => {
  const trimmed = renameValue.trim();
  if (!trimmed || trimmed === node.name) {
    // Nothing to do — just close the input
    setEditing(false);
    return;
  }

  const success = layoutEngine.renameCell(node.name, trimmed);

  if (success) {
    // If this node was the active/selected cell, update ProjectManager too
    if (node.name === activeCell) {
      ProjectManager.setSelectedCell(trimmed);
      onSelect(trimmed);   // updates parent's activeCell state
    }
    // onUpdate triggers a full tree rebuild in the parent panel
    onUpdate();
  } else {
    // Roll back the input value on failure
    setRenameValue(node.name);
    console.warn(`Rename failed: "${trimmed}" may already exist.`);
  }

  setEditing(false);
};

const handleDelete = (cell) => {
  const cellName = cell?.name || "Unknown Cell";

  if (!window.confirm(`Delete ${cellName}?`)) return;

  const allCells = layoutEngine.getAllCellsMap();

  // delete selected cell
  delete allCells[cellName];

  // remove child references
  Object.values(allCells).forEach((c) => {
    c.instances = (c.instances || []).filter(
      (inst) => inst.ref !== cellName
    );
  });

  layoutEngine.setAllCells(allCells);

  const remainingNames = Object.keys(allCells);
  const currentActive = ProjectManager.getSelectedCell();

  // if active cell deleted
  if (currentActive === cellName) {
    if (remainingNames.length > 0) {
      const nextCell = remainingNames[0];
      onSelect(nextCell);   // ✅ use existing function
    } else {
      ProjectManager.setSelectedCell(null);
      window.canvasEngine.objects = [];
      window.canvasEngine.draw();
    }
  }

  window.dispatchEvent(new Event("hierarchy-updated"));
  window.canvasEngine?.draw?.();
};  


  const countKey = parentName ? `${parentName}->${node.name}` : node.name;
  const count = counts[countKey] || 1;
  const displayName = count > 1 ? `${node.name} (${count})` : node.name;




  return (
    <div style={{ marginLeft: level * 16 }}>
      <Tooltip
      title={`Cell name : ${node.name}`}
      placement="right"
      arrow
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: node.name === activeCell ? '#333' : 'transparent',
          borderRadius: '4px',
          padding: '2px 4px',
          cursor: 'pointer',
          color: '#fff',
          fontSize: '14px',
        }}
        onClick={() => onSelect(node.name)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          {node.children.length > 0 && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
              style={{ cursor: 'pointer', fontSize: '16px' }}
            >
              {expanded ? '▾' : '▸'}
            </span>
          )}
          {/* <span>{displayName}</span> */}

          {editing ? (
  <input
    autoFocus
    value={renameValue}
    onChange={(e) => setRenameValue(e.target.value)}
    onClick={(e) => e.stopPropagation()}
    onKeyDown={(e) => {
      if (e.key === "Enter") saveRename();
      if (e.key === "Escape") setEditing(false);
    }}
    onBlur={saveRename}
    style={{
      width: '120px',
      background: '#222',
      color: '#fff',
      border: '1px solid #555'
    }}
  />
          ) : (
          <span>{displayName}</span>
          )}
        </div>

        {/* <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
          <span
            onClick={(e) => {
              e.stopPropagation();
              toggleLock(node.name);
              window.canvasEngine.draw();
              onUpdate();
            }}
            style={{ cursor: 'pointer', fontSize: '16px' }}
          >
            {isLocked(node.name) ? (
              <LockIcon style={{ fontSize: '16px' }} />
            ) : (
              <LockOpenIcon style={{ fontSize: '16px' }} />
            )}
          </span>
          <span
            onClick={(e) => {
              e.stopPropagation();
              toggleVisibility(node.name);
              window.canvasEngine.draw();
              onUpdate();
            }}
            style={{ cursor: 'pointer', fontSize: '16px' }}
          >
            {isVisible(node.name) ? (
              <VisibilityIcon style={{ fontSize: '16px' }} />
            ) : (
              <VisibilityOffIcon style={{ fontSize: '16px' }} />
            )}
          </span>
        </div> */}
        <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>

  {/* Lock / Unlock */}
  <Tooltip
    title={isLocked(node.name) ? "Unlock" : "Lock"}
    placement="top"
  >
    <span
      onClick={(e) => {
        e.stopPropagation();
        toggleLock(node.name);
        window.canvasEngine.draw();
        onUpdate();
      }}
      style={{ cursor: 'pointer', fontSize: '16px', display: 'inline-flex' }}
    >
      {isLocked(node.name) ? (
        <LockIcon style={{ fontSize: '16px' }} />
      ) : (
        <LockOpenIcon style={{ fontSize: '16px' }} />
      )}
    </span>
  </Tooltip>

  {/* Visibility On / Off */}
  <Tooltip
    title={isVisible(node.name) ? "Hide" : "Show"}
    placement="top"
  >
    <span
      onClick={(e) => {
        e.stopPropagation();
        toggleVisibility(node.name);
        window.canvasEngine.draw();
        onUpdate();
      }}
      style={{ cursor: 'pointer', fontSize: '16px', display: 'inline-flex' }}
    >
      {isVisible(node.name) ? (
        <VisibilityIcon style={{ fontSize: '16px' }} />
      ) : (
        <VisibilityOffIcon style={{ fontSize: '16px' }} />
      )}
    </span>
  </Tooltip>

  <Tooltip title="Rename Instance">
      <span
        onClick={(e) => {
          e.stopPropagation();
          setEditing(true);
          setRenameValue(node.name);
        }}
        style={{
          cursor: 'pointer',
          fontSize: '16px',
          display: 'inline-flex'
        }}
      >
        <EditIcon style={{ fontSize: '16px' }} />
      </span>
  </Tooltip>

  <Tooltip title="Delete Cell">
      <span
        onClick={(e) => {
          e.stopPropagation();
          handleDelete(node);
          alert(`Delete cell: ${node.name} (implement logic)`);
        }}
        style={{
          cursor: 'pointer',
          fontSize: '16px',
          display: 'inline-flex',
        }}
      >
        <DeleteIcon style={{ fontSize: '16px' }} />
      </span>
  </Tooltip>

</div>

      </div>
      </Tooltip>

      {expanded &&
        node.children.map((child) => (
          <TreeNode
            key={`${node.name}-${child.name}`}
            node={child}
            activeCell={activeCell}
            onSelect={onSelect}
            counts={counts}
            parentName={node.name}
            level={level + 1}
            onUpdate={onUpdate}
          />
        ))}
    </div>
  );
};

/** Main panel */
const HierarchyPanel = () => {
  const [treeRoots, setTreeRoots] = useState([]);
  const [activeCell, setActiveCell] = useState(ProjectManager.getSelectedCell());
  const [counts, setCounts] = useState({});
  const [refreshFlag, setRefreshFlag] = useState(0);
  const [startLevel, setStartLevel] = useState(0);
  const [endLevel, setEndLevel] = useState(null); // start as null until we compute
  const [searchTerm, setSearchTerm] = useState('');



  // useEffect(() => {
  //   const rebuildTree = () => {
  //     const { roots, counts } = buildHierarchyTree();
  //     setTreeRoots(roots);
  //     setCounts(counts);

  //     if (!ProjectManager.getSelectedCell() && roots.length > 0) {
  //       const defaultCell = roots[0].name;
  //       ProjectManager.setSelectedCell(defaultCell);
  //       setActiveCell(defaultCell);

  //       const rootCell =
  //         layoutEngine.getCellByName(defaultCell) ||
  //         layoutEngine.getUploadedCell(defaultCell);

  //       const maxDepth = getMaxLevel(rootCell);

  //       setStartLevel(0);
  //       setEndLevel(maxDepth); // ✅ default to max level like KLayout

  //       if (window.canvasEngine?.setLevelRange) {
  //         window.canvasEngine.setLevelRange(0, maxDepth);
  //       }
  //       window.canvasEngine.draw();
  //     }
  //   };

  //   rebuildTree();
  //   window.addEventListener('hierarchy-updated', rebuildTree);
  //   return () => window.removeEventListener('hierarchy-updated', rebuildTree);
  // }, [refreshFlag]);

    useEffect(() => {
    const rebuildTree = () => {
      const { roots, counts } = buildHierarchyTree();
      setTreeRoots(roots);
      setCounts(counts);

      // If there's already a selected cell, use it to compute endLevel immediately
      const selectedCell = ProjectManager.getSelectedCell();

      if (selectedCell) {
        setActiveCell(selectedCell);

        const selCell =
          layoutEngine.getCellByName(selectedCell) ||
          layoutEngine.getUploadedCell(selectedCell);

        if (selCell) {
          const maxDepth = getMaxLevel(selCell);
          setStartLevel(0);
          setEndLevel(maxDepth);

          if (window.canvasEngine?.setLevelRange) {
            window.canvasEngine.setLevelRange(0, maxDepth);
          }
          window.canvasEngine?.draw?.();
        }
        return;
      }

      // No selected cell: pick the first root (if any) as before
      if (roots.length > 0) {
        const defaultCell = roots[0].name;
        ProjectManager.setSelectedCell(defaultCell);
        setActiveCell(defaultCell);

        const rootCell =
          layoutEngine.getCellByName(defaultCell) ||
          layoutEngine.getUploadedCell(defaultCell);

        if (rootCell) {
          const maxDepth = getMaxLevel(rootCell);

          setStartLevel(0);
          setEndLevel(maxDepth);

          if (window.canvasEngine?.setLevelRange) {
            window.canvasEngine.setLevelRange(0, maxDepth);
          }
          window.canvasEngine?.draw?.();
        }
      } else {
        // no roots: clear levels
        setStartLevel(0);
        setEndLevel(null);
      }
    };

    rebuildTree();
    window.addEventListener('hierarchy-updated', rebuildTree);
    return () => window.removeEventListener('hierarchy-updated', rebuildTree);
  }, [refreshFlag]);


  useEffect(() => {
    if (endLevel !== null && window.canvasEngine?.setLevelRange) {
      window.canvasEngine.setLevelRange(startLevel, endLevel);
      window.canvasEngine.draw();
    }
  }, [startLevel, endLevel]);

  const handleSelect = (cellName) => {
    setActiveCell(cellName);
    ProjectManager.setSelectedCell(cellName);
    window.canvasEngine.FeatureRenderer.loadSceneForCell(cellName); // cell update for bug&imp rishabh


    const rootCell =
      layoutEngine.getCellByName(cellName) ||
      layoutEngine.getUploadedCell(cellName);

    const maxDepth = getMaxLevel(rootCell);

    setEndLevel(maxDepth); // ✅ reset to maxDepth when selecting a new cell

    if (window.canvasEngine?.setLevelRange) {
      window.canvasEngine.setLevelRange(startLevel, maxDepth);
    }
    window.canvasEngine.draw();
  };

  const filterTree = (nodes, term) => {
  if (!term) return nodes;

  return nodes
    .map(node => {
      const children = filterTree(node.children, term);

      // ✅ if this node matches, include ALL of its children (unfiltered)
      if (node.name.toLowerCase().includes(term.toLowerCase())) {
        return { ...node, children: node.children };
      }

      // ✅ otherwise include only filtered children (if any matched)
      if (children.length > 0) {
        return { ...node, children };
      }

      return null;
    })
    .filter(Boolean);
};
  const filteredRoots = filterTree(treeRoots, searchTerm);


  return (
  <div style={{ display: 'flex', flexDirection: 'column', height: '100%', color: '#fff' }}>
    
    {/* 🔍 Search bar at top */}
    <div style={{ padding: '6px', borderBottom: '1px solid #444' }}>
      <input
        type="text"
        placeholder="Search cell or hierarchy..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{
          width: '100%',
          padding: '6px 8px',
          borderRadius: '6px',
          border: '1px solid #555',
          backgroundColor: '#222',
          color: '#fff',
          fontSize: '14px'
        }}
      />
    </div>

    {/* 🌲 Hierarchy tree area */}
    <div style={{ flex: 1, overflowY: 'auto', padding: '5px' }}>
      {filteredRoots.length === 0 ? (
        <div style={{ color: '#999', textAlign: 'center', marginTop: '10px' }}>
          No matching cells found
        </div>
      ) : (
        filteredRoots.map((root) => (
          <TreeNode
            key={root.name}
            node={root}
            activeCell={activeCell}
            onSelect={handleSelect}
            counts={counts}
            onUpdate={() => setRefreshFlag((f) => f + 1)}
            searchTerm={searchTerm}
          />
        ))
      )}
    </div>

    {/* 🎚 Level range controls at bottom */}
    <div style={{ padding: '6px', display: 'flex', gap: '6px', borderTop: '1px solid #444' }}>
      <Tooltip title="Set Starting Hierarchy Level" placement="top" arrow>
      <label>
        Start:
        <input
          type="number"
          value={startLevel}
          min={0}
          onChange={(e) => setStartLevel(parseInt(e.target.value, 10))}
          style={{ width: '60px', marginLeft: '4px' }}
        />
      </label>
      </Tooltip>

      <Tooltip title="Set Ending Hierarchy Level" placement="top" arrow>
      <label>
        End:
        <input
          type="number"
          value={endLevel}
          min={startLevel}
          onChange={(e) => setEndLevel(parseInt(e.target.value, 10))}
          style={{ width: '60px', marginLeft: '4px' }}
        />
      </label>
      </Tooltip>
    </div>
  </div>
);

  // return (
  //   <div style={{ display: 'flex', flexDirection: 'column', height: '100%', color: '#fff' }}>
  //     <div style={{ flex: 1, overflowY: 'auto', padding: '5px' }}>
  //       {treeRoots.map((root) => (
  //         <TreeNode
  //           key={root.name}
  //           node={root}
  //           activeCell={activeCell}
  //           onSelect={handleSelect}
  //           counts={counts}
  //           onUpdate={() => setRefreshFlag((f) => f + 1)}
  //         />
  //       ))}
  //     </div>

  //     {/* Level range controls */}
  //     <div style={{ padding: '6px', display: 'flex', gap: '6px' }}>
  //       <label>
  //         Start:
  //         <input
  //           type="number"
  //           value={startLevel}
  //           min={0}
  //           onChange={(e) => setStartLevel(parseInt(e.target.value, 10))}
  //           style={{ width: '60px', marginLeft: '4px' }}
  //         />
  //       </label>
  //       <label>
  //         End:
  //         <input
  //           type="number"
  //           value={endLevel ?? 0}
  //           min={startLevel}
  //           onChange={(e) => setEndLevel(parseInt(e.target.value, 10))}
  //           style={{ width: '60px', marginLeft: '4px' }}
  //         />
  //       </label>
  //     </div>
  //   </div>
  // );
};

export default HierarchyPanel;
